import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { Icons, Alert, Box, Typography, Chip } from "@curves/ui-base";
import { EditableInfo, OverflowMenu } from "@icoach/components/";
import { useMemberDetailContext } from "@icoach/memberDetail/MemberDetailContext";
import useMemberCyclePurchaseApi from "@apis/useMemberCyclePurchaseApi";
import { displayDateText } from "@util/moment";
import useShowPDF from "@util/hook/useShowPDF";
import { filterMenu, getEffectOrderMenuMap } from "@icoach/healthyCyclePurchase/CyclePurchase.util";
import { EFFECT_CONTENT_TYPE, EFFECT_STATUS } from "@constants/";
import { isFunction, isEmpty } from "@util/utils";

// 變更單
export const CycleEffectOrderCard = ({ title = "預計變更單", sourceData = {}, cycleOrderID, refresh, dialogRef }) => {
	const { effectDate, effectStatusText, effectCreateDate, employeeName, effectTypeText = [], effectType = [], effectStatus } = sourceData;
	// 暫停單且生效中時，提示需由變更單恢復暫停
	const isSuspendValid = effectType.includes(EFFECT_CONTENT_TYPE.STOP) && effectStatus === EFFECT_STATUS.VALID;
	const showPDF = useShowPDF();

	const menuData = useMemo(
		() => {
			return filterMenu(
				getEffectOrderMenuMap(
					{
						...sourceData,
						cycleOrderID,
					},
					{ dialogRef, refresh, showPDF },
				),
			);
		},
		// eslint-disable-next-line
		[sourceData, cycleOrderID, refresh],
	);

	return (
		<React.Fragment>
			<Box className={"minor-title"}>
				<Icons.DocumentFile />
				<Typography>{title}</Typography>
			</Box>
			<Box className={"cycle-order-card minor"}>
				<Box className={"cycle-order-card-header"}>
					<Grid spacing={3} container>
						<Grid xs={4} item>
							<EditableInfo label={"生效日期"}>{displayDateText(effectDate)}</EditableInfo>
						</Grid>
						<Grid xs={4} item>
							<EditableInfo label={"建單日期"}>{displayDateText(effectCreateDate)}</EditableInfo>
						</Grid>
						<Grid xs={4} item>
							<EditableInfo label={"建單擔當"}>{employeeName}</EditableInfo>
						</Grid>
						<Grid xs={4} item>
							<EditableInfo label={"狀態"}>{effectStatusText}</EditableInfo>
						</Grid>
						<Grid xs={8} item>
							<EditableInfo label={"變更項目"}>
								<span className={"font-weight-bold"}>{Array.isArray(effectTypeText) && effectTypeText.join("、")}</span>
							</EditableInfo>
						</Grid>
					</Grid>
					<OverflowMenu actionButtonProps={{ className: "cycle-order-card-header-actions" }} menuData={menuData} enabled={!isEmpty(menuData)} />
					{isSuspendValid && <Chip className={"decorate-delete-chip"} label={"請由變更單恢復暫停"} color={"error"} />}
				</Box>
			</Box>
		</React.Fragment>
	);
};

const PendingCycleEffectOrderList = React.forwardRef(({ memberID, cycleOrderID, dialogRef }, ref) => {
	const { refresh } = useMemberDetailContext();
	const { getMemberCycleOrderFutureApi } = useMemberCyclePurchaseApi();
	const [data, setData] = useState([]);

	const doMemberCycleOrderFutureApi = async (memberID, cycleOrderID) => {
		let res = await getMemberCycleOrderFutureApi(memberID, cycleOrderID);
		if (res) {
			setData(res);
			return res;
		}
	};

	const refreshFn = useCallback(
		() => {
			isFunction(refresh) && refresh();
			if (memberID && cycleOrderID) {
				doMemberCycleOrderFutureApi(memberID, cycleOrderID);
			}
		},
		// eslint-disable-next-line
		[memberID, cycleOrderID],
	);

	useImperativeHandle(
		ref,
		() => ({
			doMemberCycleOrderFutureApi,
		}),
		// eslint-disable-next-line
		[],
	);

	useEffect(() => {
		if (memberID && cycleOrderID) {
			doMemberCycleOrderFutureApi(memberID, cycleOrderID);
		}
		// eslint-disable-next-line
	}, [memberID, cycleOrderID]);

	return (
		<React.Fragment>
			{!isEmpty(data) ? (
				<Box component={"ul"} className="minor-orders mb-4">
					{data.map((item, i) => (
						<Box component={"li"} key={i} className="minor-order-item">
							<CycleEffectOrderCard sourceData={item} cycleOrderID={cycleOrderID} refresh={refreshFn} dialogRef={dialogRef} />
						</Box>
					))}
				</Box>
			) : (
				<Alert className={"mb-4"} color="info" severity="info" variant="filled">
					尚無資料
				</Alert>
			)}
		</React.Fragment>
	);
});

PendingCycleEffectOrderList.displayName = "PendingCycleEffectOrderList";

export default PendingCycleEffectOrderList;
